import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Collapse,
  IconButton,
  Chip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  RocketLaunch as RocketIcon
} from '@mui/icons-material';
import { activationService } from '../services/activationService';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface OnboardingChecklistProps {
  siteId: number;
  planTier?: string;
  onComplete?: () => void;
}

const isStarterPlan = (planTier?: string): boolean => {
  const normalized = (planTier || '').trim().toUpperCase();
  return normalized === 'STARTER' || normalized === 'FREE';
};

export const OnboardingChecklist: React.FC<OnboardingChecklistProps> = ({ siteId, planTier, onComplete }) => {
  const starterPlan = isStarterPlan(planTier);
  const [expanded, setExpanded] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [steps, setSteps] = useState<OnboardingStep[]>([
    {
      id: 'connect-site',
      title: 'Connect to your website',
      description: 'Add your site URL and verify ownership',
      completed: false,
      action: {
        label: 'Add Site',
        onClick: () => window.location.href = '/sites/add'
      }
    },
    {
      id: 'verify-gsc',
      title: 'Connect Google Search Console',
      description: 'Link your GSC account for search data',
      completed: false,
      action: {
        label: 'Connect GSC',
        onClick: () => window.location.href = '/settings/integrations'
      }
    },
    {
      id: 'explore-dashboard',
      title: 'Explore your dashboard',
      description: 'Take a tour of the main dashboard features',
      completed: false,
      action: {
        label: 'View Dashboard',
        onClick: () => window.location.href = '/early-stage/dashboard'
      }
    },
    {
      id: 'view-pillar',
      title: 'Review a readiness pillar',
      description: 'Understand your SEO health in each area',
      completed: false
    },
    {
      id: 'complete-action',
      title: 'Complete your first action',
      description: 'Implement a recommended SEO improvement',
      completed: false
    },
    {
      id: 'view-timeline',
      title: 'Explore your SEO Timeline',
      description: 'See your week-by-week journey and track your SEO progress over time',
      completed: false,
      action: {
        label: 'View Timeline',
        onClick: () => window.location.href = '/early-stage/timeline'
      }
    }
  ]);

  // Load completion status from activation metrics
  useEffect(() => {
    const loadProgress = async () => {
      try {
        const metrics = await activationService.getMetrics(siteId);
        
        setSteps((prev) =>
          prev.map((step) => {
            switch (step.id) {
              case 'connect-site':
                return { ...step, completed: starterPlan || metrics.day7.siteConnected };
              case 'verify-gsc':
                return { ...step, completed: metrics.day7.siteConnected }; // Assume same as site connection
              case 'explore-dashboard':
                return { ...step, completed: metrics.day7.dashboardViews >= (starterPlan ? 1 : metrics.day7.dashboardViewsRequired) };
              case 'view-pillar':
                return { ...step, completed: metrics.day7.pillarsViewed >= 1 };
              case 'complete-action':
                return { ...step, completed: metrics.day7.actionsCompleted >= 1 };
              case 'view-timeline':
                return { ...step, completed: metrics.day7.timelineViewed };
              default:
                return step;
            }
          })
        );
      } catch (error) {
        console.error('Failed to load onboarding progress:', error);
      }
    };

    loadProgress();
  }, [siteId, starterPlan]);

  // Check if dismissed
  useEffect(() => {
    const dismissed = localStorage.getItem(`onboarding-dismissed-${siteId}`);
    if (dismissed) {
      setDismissed(true);
    }
  }, [siteId]);

  const handleDismiss = () => {
    localStorage.setItem(`onboarding-dismissed-${siteId}`, 'true');
    setDismissed(true);
  };

  const visibleSteps = steps.filter((step) => !starterPlan || (step.id !== 'verify-gsc' && step.id !== 'view-timeline'));
  const completedCount = visibleSteps.filter((s) => s.completed).length;
  const progress = visibleSteps.length === 0 ? 0 : (completedCount / visibleSteps.length) * 100;
  const isComplete = visibleSteps.length > 0 && completedCount === visibleSteps.length;

  // Auto-hide when complete
  useEffect(() => {
    if (isComplete && onComplete) {
      setTimeout(() => {
        onComplete();
        setDismissed(true);
      }, 3000);
    }
  }, [isComplete, onComplete]);

  if (dismissed) return null;

  return (
    <Card
      sx={{
        mb: 3,
        border: '2px solid',
        borderColor: isComplete ? 'success.main' : 'primary.main',
        bgcolor: isComplete ? 'success.light' : 'background.paper'
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <RocketIcon color={isComplete ? 'success' : 'primary'} />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {isComplete ? '🎉 Onboarding Complete!' : 'Getting Started'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isComplete
                  ? 'You\'re all set! Continue exploring the platform.'
                  : `${completedCount} of ${visibleSteps.length} steps completed`}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <IconButton size="small" onClick={handleDismiss}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 8,
            borderRadius: 4,
            mb: 2,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              bgcolor: isComplete ? 'success.main' : 'primary.main'
            }
          }}
        />

        <Collapse in={expanded}>
          <List dense disablePadding>
            {visibleSteps.map((step, index) => (
              <ListItem
                key={step.id}
                disablePadding
                sx={{
                  py: 1,
                  opacity: step.completed ? 0.7 : 1
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {step.completed ? (
                    <CheckCircleIcon color="success" />
                  ) : (
                    <UncheckedIcon color="disabled" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography
                        variant="body2"
                        fontWeight={step.completed ? 400 : 600}
                        sx={{
                          textDecoration: step.completed ? 'line-through' : 'none'
                        }}
                      >
                        {step.title}
                      </Typography>
                      {!step.completed && index === 0 && (
                        <Chip label="Start here" size="small" color="primary" />
                      )}
                    </Box>
                  }
                  secondary={step.description}
                />
                {step.action && !step.completed && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={step.action.onClick}
                    sx={{ ml: 2 }}
                  >
                    {step.action.label}
                  </Button>
                )}
              </ListItem>
            ))}
          </List>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default OnboardingChecklist;
